import { defaultUpscaleLimits, type UpscaleCapabilities, type UpscaleJob, type UpscaleLimits, type UpscaleScale, type UpscaleStatus } from './domain';

export interface UpscalerRepository {
  readonly canonicalHref?: string;
  capabilities(signal?: AbortSignal): Promise<UpscaleCapabilities>;
  create(file: Blob, model: string, scale: UpscaleScale, requestId: string, signal?: AbortSignal): Promise<UpscaleJob>;
  status(receipt: string, signal?: AbortSignal): Promise<UpscaleStatus>;
  result(receipt: string, maximum: number, signal?: AbortSignal): Promise<Blob>;
  cancel(receipt: string): Promise<void>;
}
const messages: Record<string, string> = {
  quota_exceeded: 'Đã hết lượt xử lý. Vui lòng thử lại sau.',
  not_configured: 'Dịch vụ upscale chưa được cấu hình.',
  unavailable: 'Dịch vụ upscale tạm thời không sẵn sàng.',
  budget_exhausted: 'Ngân sách khả dụng chưa đủ cho lượt mới và mức dự phòng 100 token.',
  budget_unavailable: 'Chưa xác nhận được ngân sách. Model tạm khóa để bảo vệ chi phí.',
  expired: 'Phiên xử lý đã hết hạn. Hãy thử lại.',
  invalid_image: 'Ảnh không hợp lệ hoặc vượt giới hạn xử lý. Hãy chọn ảnh khác.',
  too_large: 'Ảnh phải nhỏ hơn hoặc bằng 10 MB.',
  start_uncertain: 'Chưa xác nhận máy chủ đã nhận ảnh. Xử lý có thể vẫn tiếp tục; tránh gửi lại ngay.',
  start_pending: 'Yêu cầu đang được khởi tạo. Vui lòng chờ.',
  poll_too_fast: 'Đang kiểm tra quá nhanh. Vui lòng chờ rồi thử lại.',
  result_limit: 'Đã hết lượt lấy kết quả của phiên này.',
  not_ready: 'Ảnh kết quả chưa sẵn sàng.',
  request_conflict: 'Yêu cầu bị trùng. Vui lòng bắt đầu phiên mới.',
};
async function checked(response: Response) {
  if (response.ok) return response;
  let code = '';
  try { code = (await response.json()).code; } catch { /* Untrusted upstream text is never shown. */ }
  throw new Error(messages[code] ?? (response.status === 429 ? messages.quota_exceeded : 'Không thể xử lý ảnh. Vui lòng thử lại.'));
}
const invalid = () => new Error('Phản hồi từ dịch vụ không hợp lệ.');
export function createUpscalerRepository(fetcher: typeof fetch = fetch, hostname = typeof window === 'undefined' ? 'localhost' : window.location.hostname): UpscalerRepository {
  const mirror = !['designforge.site', 'designforge-8km.pages.dev', 'localhost', '127.0.0.1', '::1', '[::1]'].includes(hostname.toLowerCase());
  const canonicalHref = mirror ? 'https://designforge.site/tools/ai-image-upscaler/' : undefined;
  async function request(path: string, options: RequestInit, timeout: number) {
    if (mirror) throw new Error('Công cụ này cần mở trên DesignForge.');
    const signal = options.signal;
    try {
      return await fetcher(path, { ...options, signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(timeout)]) : AbortSignal.timeout(timeout) });
    } catch (error) {
      if (signal?.aborted) throw error;
      throw new Error('Mất kết nối với dịch vụ. Vui lòng thử lại.');
    }
  }
  const post = (path: string, receipt: string, signal?: AbortSignal) => request(`/api/upscaler/jobs/${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ receipt }), signal, credentials: 'same-origin', cache: 'no-store',
  }, path === 'result' ? 180000 : 20000).then(checked);
  return {
    canonicalHref,
    async capabilities(signal) {
      if (mirror) return { available: false, reason: 'unavailable', models: [{ id: 'realesrgan', name: 'Real-ESRGAN', scales: [2, 4] }], limits: { ...defaultUpscaleLimits }, budget: null };
      const data = await (await checked(await request('/api/upscaler/capabilities', { signal, credentials: 'same-origin', cache: 'no-store' }, 35000))).json();
      if (typeof data.available !== 'boolean' || !Array.isArray(data.models) || !data.limits) throw invalid();
      const limits = data.limits as UpscaleLimits;
      for (const key of Object.keys(defaultUpscaleLimits) as (keyof UpscaleLimits)[]) if (!Number.isSafeInteger(limits[key]) || limits[key] <= 0 || limits[key] > defaultUpscaleLimits[key]) throw invalid();
      if (!data.models.every((model: { id?: unknown; name?: unknown; scales?: unknown }) => typeof model.id === 'string' && typeof model.name === 'string' && Array.isArray(model.scales) && model.scales.length > 0 && model.scales.every(scale => scale === 2 || scale === 4))) throw invalid();
      if (data.available && !data.models.length) throw invalid();
      if (data.reason !== undefined && !['not_configured', 'unavailable', 'budget_exhausted', 'budget_unavailable'].includes(data.reason)) throw invalid();
      const budget = data.budget ?? null;
      if (budget !== null) {
        if (typeof budget !== 'object' || !Number.isSafeInteger(budget.remainingTokens) || budget.remainingTokens < 0 || budget.remainingTokens > 3000 || budget.totalTokens !== 3000 || budget.minimumTokens !== 100 || typeof budget.updatedAt !== 'string' || !Number.isFinite(Date.parse(budget.updatedAt)) || !(budget.resetAt === null || typeof budget.resetAt === 'string' && Number.isFinite(Date.parse(budget.resetAt)))) throw invalid();
      }
      // Older responses or unavailable accounting must never silently unlock a paid model.
      if (data.available && budget === null) return { ...data, budget: null, available: false, reason: 'budget_unavailable' } as UpscaleCapabilities;
      if (data.available && budget.remainingTokens < budget.minimumTokens) return { ...data, budget, available: false, reason: 'budget_exhausted' } as UpscaleCapabilities;
      return { ...data, budget } as UpscaleCapabilities;
    },
    async create(file, model, scale, requestId, signal) {
      const response = await checked(await request('/api/upscaler/jobs', {
        method: 'POST', body: file, signal, credentials: 'same-origin', cache: 'no-store',
        headers: { 'Content-Type': file.type, 'X-Upscaler-Model': model, 'X-Upscaler-Scale': String(scale), 'X-Request-ID': requestId },
      }, 180000));
      const data = await response.json();
      if (typeof data.receipt !== 'string' || !data.receipt || data.status !== 'queued' || typeof data.expiresAt !== 'string' || !Number.isFinite(Date.parse(data.expiresAt))) throw invalid();
      return data as UpscaleJob;
    },
    async status(receipt, signal) {
      const data = await (await post('status', receipt, signal)).json();
      if (!['queued', 'running', 'succeeded', 'failed', 'cancelled'].includes(data.status)) throw invalid();
      return data.status as UpscaleStatus;
    },
    async result(receipt, maximum, signal) {
      const response = await post('result', receipt, signal);
      if (response.headers.get('content-type')?.split(';')[0].trim() !== 'image/png') throw invalid();
      const declared = Number(response.headers.get('content-length'));
      if (declared > maximum) { await response.body?.cancel(); throw invalid(); }
      const reader = response.body?.getReader();
      if (!reader) throw invalid();
      const parts: Uint8Array<ArrayBuffer>[] = [];
      let bytes = 0;
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          bytes += value.byteLength;
          if (bytes > maximum) { await reader.cancel(); throw invalid(); }
          parts.push(new Uint8Array(value));
        }
      } finally { reader.releaseLock(); }
      if (!bytes) throw invalid();
      return new Blob(parts, { type: 'image/png' });
    },
    async cancel(receipt) {
      const signal = AbortSignal.timeout(10000);
      const data = await (await post('cancel', receipt, signal)).json();
      if (data.status !== 'cancel_requested') throw invalid();
    },
  };
}

export const upscalerRepository = createUpscalerRepository();

export function waitForPoll(signal: AbortSignal, milliseconds = 10000) {
  return new Promise<void>((resolve, reject) => {
    const abort = () => { clearTimeout(timer); signal.removeEventListener('abort', abort); reject(new DOMException('Aborted', 'AbortError')); };
    const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, milliseconds);
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
  });
}
